import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import { Flame, Check } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
    const { token } = useParams();
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [accepting, setAccepting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            // Save token and redirect to login
            localStorage.setItem("sw_pending_invite", token);
            navigate("/signup", { state: { invite: token } });
        }
    }, [loading, user, navigate, token]);

    const accept = async () => {
        setAccepting(true);
        setError(null);
        try {
            await axios.post(`${API}/team/accept/${token}`, null, {
                headers: { Authorization: `Bearer ${localStorage.getItem("sw_token")}` },
            });
            setDone(true);
            localStorage.removeItem("sw_pending_invite");
            toast.success("Joined the team. Welcome aboard.");
            setTimeout(() => navigate("/app"), 1200);
        } catch (err) {
            setError(err?.response?.data?.detail || "Could not accept invite");
        } finally { setAccepting(false); }
    };

    if (loading) return <div className="min-h-screen bg-[#0a0a0a] text-zinc-500 flex items-center justify-center">Loading…</div>;
    if (!user) return null;

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6 relative overflow-hidden">
            <div className="absolute inset-0 orange-glow pointer-events-none" />
            <div className="w-full max-w-md relative">
                <Link to="/" className="flex items-center gap-2 mb-10">
                    <Flame className="w-5 h-5 text-signal" />
                    <span className="font-heading font-black text-xl tracking-tight">sales<span className="text-signal">whisper</span></span>
                </Link>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-2">/ TEAM INVITE</div>
                {done ? (
                    <>
                        <h1 className="font-heading font-bold text-4xl mb-4 flex items-center gap-3">
                            <Check className="w-8 h-8 text-signal" /> You're in.
                        </h1>
                        <p className="text-zinc-400">Redirecting you to the app…</p>
                    </>
                ) : (
                    <>
                        <h1 className="font-heading font-bold text-4xl mb-3">Join the team.</h1>
                        <p className="text-zinc-400 mb-8 text-sm">
                            You've been invited to join a SalesWhisper team. Accepting will move your account to the team's shared plan.
                        </p>
                        {error && (
                            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-md text-sm text-red-300" data-testid="accept-error">
                                {error}
                            </div>
                        )}
                        <button
                            onClick={accept}
                            disabled={accepting}
                            data-testid="accept-invite-btn"
                            className="w-full bg-signal hover:bg-signal-hover text-white px-6 py-3.5 rounded-md font-semibold transition-colors disabled:opacity-60"
                        >
                            {accepting ? "Joining…" : "Accept invite"}
                        </button>
                        <p className="text-xs text-zinc-500 mt-6">
                            Signed in as <span className="text-white">{user.email}</span>.{" "}
                            <button onClick={() => { localStorage.removeItem("sw_token"); window.location.reload(); }} className="text-signal hover:underline">
                                Use a different account
                            </button>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
